import {
  ObjectField,
  ObjectFieldCellDefinition,
  ObjectFieldCellRawDefinition,
  ObjectFieldCells,
  ObjectFieldsRawData,
  ObjectFieldStatus
} from '../interfaces/object-field';
import {
  ObjectFieldCellType,
  ObjectFieldsMetaData,
  ObjectFieldsRawMetaData
} from '../interfaces/object-field-header';
import { PIPELINE_LOAD_MODE_DESCRIPTION } from '../../PipelineSettings/constants/pipeline-settings';
import { PipelineLoadMode } from '../../../shared/interfaces/integration';

export function ObjectFieldsMetaDataFactory(
  rawFieldsMeta: ObjectFieldsRawMetaData
): ObjectFieldsMetaData {
  const columnConfigs = Object.entries(rawFieldsMeta.data.column_configs).map(([key, config]) => ({
    name: key,
    displayName: config.display_name,
    description: config.description,
    isEditable: config.is_editable,
    order: config.order,
    type: config.type as ObjectFieldCellType
  }));

  columnConfigs.sort((a, b) => a.order - b.order);

  return {
    columnConfigs,
    objectConfigs: Object.entries(rawFieldsMeta.data.object_configs).map(([key, config]) => ({
      key,
      displayName: config.display_name,
      isEditable: config.is_editable,
      options: Object.entries(config.additional_config).map(([optionKey, option]) => ({
        value: optionKey,
        label: option.display_name,
        description:
          key === 'load_mode'
            ? PIPELINE_LOAD_MODE_DESCRIPTION[optionKey as PipelineLoadMode]
            : undefined
      }))
    }))
  };
}

export function ObjectFieldsFactory(fieldsRawData: ObjectFieldsRawData): ObjectField[] {
  return fieldsRawData.fields.map<ObjectField>(({ key, value: rawCells }) => {
    const cellsWithStatus: ObjectFieldCells = Object.entries(rawCells).reduce(
      (acc, [cellKey, cellValue]) => {
        return {
          ...acc,
          [cellKey]: FieldCellDefinitionFactory(cellValue)
        };
      },
      {}
    );

    const { status, ...cellsWithoutStatus } = cellsWithStatus;

    return {
      cells: cellsWithoutStatus,
      fieldName: key,
      status: status as ObjectFieldCellDefinition<ObjectFieldStatus>,
      isConfigurable: ![
        ObjectFieldStatus.DELETED,
        ObjectFieldStatus.INACCESSIBLE,
        ObjectFieldStatus.INCONSISTENT,
        ObjectFieldStatus.DISABLED
      ].includes(status.value as ObjectFieldStatus)
    };
  });
}

function FieldCellDefinitionFactory<T>(
  fieldCellRawDefinition: ObjectFieldCellRawDefinition<T>
): ObjectFieldCellDefinition<T> {
  return {
    value: fieldCellRawDefinition.value,
    helperText: fieldCellRawDefinition.helper_text,
    statusReason: fieldCellRawDefinition.reason,
    isEditable: fieldCellRawDefinition.is_editable
  };
}
